import api from "@/api";
import {
  Container_Type,
  type ActivityInput,
  type CategoryInput,
  type CoreNewsInput,
  type Document,
  type EmployeeInput,
  type Maybe,
  type NewsInput,
} from "@/graphql/generated-types";
import toImageList, { type MediaHolder } from "@/helpers/files/toImageList";
import type { IFileState, IImage } from "@/interfaces";

export type PicturesFeatureState = {
  image?: string | IImage;
  imageList: IFileState[];
};

export type DatabaseModel = {
  documents?: Maybe<Array<Document>>;
};

export type SaveFeatureProps<State> = {
  store: {
    item: State;
  };
  input: {
    image?:
      | NewsInput["image"]
      | ActivityInput["image"]
      | CategoryInput["image"]
      | EmployeeInput["image"]
      | CoreNewsInput["image"];
    pictures?:
      | NewsInput["pictures"]
      | ActivityInput["pictures"]
      | CoreNewsInput["pictures"];
  };
  container: Container_Type;
  institutionId: string;
  clearEmptyPictures?: boolean;
  ignoreImageProp?: boolean;
};

export const PICTURES_FEATURE = {
  getDefault() {
    return {
      image: undefined,
      imageList: [],
    };
  },

  getState(data: MediaHolder): PicturesFeatureState {
    return {
      imageList: toImageList(data),
      image: data.image?.id,
    };
  },

  hash(state: PicturesFeatureState) {
    return {
      imageList: state.imageList,
    };
  },

  toValidation(state: PicturesFeatureState) {
    return {
      imageList: state.imageList.filter((item) => item.action !== "delete"),
    };
  },

  async save({
    store,
    input,
    container,
    institutionId,
    clearEmptyPictures,
    ignoreImageProp,
  }: SaveFeatureProps<PicturesFeatureState>) {
    // These are the images that are already uploaded expect the cover image.
    input.pictures = store.item.imageList
      .filter((item) => !item.action && !item.isFavorite && item.id)
      .map((item) => item.id) as string[];

    // This is the cover image that is no need to upload
    input.image =
      store.item.imageList.find(
        (item) => item.isFavorite && !item.action && item.id !== "-1",
      )?.id || null;

    if (ignoreImageProp) {
      input.image = undefined;
    }

    // Which images should be uploaded
    const imagesToUploads = store.item.imageList.filter(
      (item) => item.action === "upload" && !item.isDefault,
    );

    const requests: Promise<string | undefined | null>[] = [];

    // Creating parallel requests
    for (const imageToUpload of imagesToUploads) {
      requests.push(
        api.resource.save({
          container,
          body: imageToUpload.base64 || undefined,
          url: imageToUpload.url || undefined,
          institution: institutionId,
        }),
      );
    }

    // Sending them all together
    const results = await Promise.all(requests);

    // Using returned ids
    imagesToUploads.forEach((imageToUpload, index) => {
      const id = results[index];

      if (!id) {
        throw new Error("File upload error");
      }

      // We should use the cover image property if the uploaded image is a favorite
      if (imageToUpload.isFavorite) {
        input.image = id;
      } else {
        if (!input.pictures) {
          input.pictures = [];
        }

        input.pictures.push(id);
      }
    });

    if (clearEmptyPictures && input.pictures.length === 0) {
      delete input.pictures;
    }
  },
};
